
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import getT from 'next-translate/getT';
import CategorySection from 'components/CategorySections/CategorySection';
import Layout from 'components/Layout';
import {
  getCastImg,
  isMobileView,
  Permalink,
  sortByKeys,
  formatYear,
  sec2time,
  createRedirectIfNeeded,
  imageUrl,
} from 'helpers';
import { SimilarPlayType, PlayBase, Theatre as TheatreApi } from 'typings/api';
import { StaticPathParams } from 'typings/staticPaths';
import styles from './index.module.scss';
import { Role } from 'typings/notGenerated';
import CastPopup from 'components/Popups/CastPopup';
import AppLink from 'components/AppLink/AppLink';
import PlayButton from 'components/buttons/PlayButton/PlayButton';
import { basicSEO } from 'helpers/jsx';
import { useAuth, usePopup, withForcePlanToLoggedInUnsubs } from 'context';
import Analytics from 'helpers/Analytics';
import { Namespace, PlaylistType, PopupType } from 'enums';
import Play from 'models/Play';
import { Play as PlayApi } from 'typings/api';
import Theatre from 'models/Theatre';
import { useLang } from 'context/langContext';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { locales } from 'i18n';
import PlatformHelper from 'helpers/PlatformHelper';
import classNames from 'classnames';
import { useNotificationContext } from 'context';
import { getSharedApiClient } from '../../../../api/client';
import { baseUrl } from 'config/routeTable';

const FIRST_ROLES = ['original-author', 'director', 'actor'];
interface T {
  premiere: string;
  genres: string;
  awards: string;
  showAll: string;
  cast: string;
  theatreMore: string;
  sameCast: string;
  similarGenre: string;
  support: string;
  trailer: string;
  supportTheater: string;
  watchList: string;
  onWatchList: string;
  addedToWatchList: string;
  removedFromWatchList: string;
}
interface Props {
  play: PlayApi | undefined;
  similarGenresPlays: PlayBase[];
  similarCastPlays: PlayBase[];
  moreFromTheatre: PlayBase[];
  translations: T;
  countryCode: string;
  canonical: string;
}

const PerformanceDetail: NextPage<Props> = ({
  play,
  similarGenresPlays,
  similarCastPlays,
  moreFromTheatre,
  translations,
  countryCode,
  canonical,
}) => {
  const { t } = useTranslation();
  const [castDialogShowed, setCastDialogShowed] = useState(false);
  const [castListClass, setCastListClass] = useState('');
  const [castSectionCount, setCastSectionCount] = useState(2);
  const [smallerHeadingCssClass, setSmallerHeadingCssClass] = useState(false);
  const { locale } = useLang();
  const { user } = useAuth();
  const { setPopupConfig } = usePopup();
  const { createNotification } = useNotificationContext();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [videoDetails, setVideoDetails] = useState<PlayApi>();

  const elH1 = useRef<HTMLHeadingElement>(null);

  const [onWatchlist, setOnWatchlist] = useState(false);

  useEffect(() => {
    if (user && play) {
      getSharedApiClient().getPlayDetail(play.id).then(setVideoDetails);
    }
  }, [user, play]);

  useEffect(() => {
    // prepare data for cast section
    let sectionCounter = 0;
    let listClassTmp = '';
    if (play) {
      const rolesCount = play.roles?.length;
      if (rolesCount > 4) {
        listClassTmp = styles.ommited2;
        sectionCounter = 4;
      } else if (rolesCount > 3) {
        listClassTmp = styles.ommited3;
        sectionCounter = 4;
      } else {
        listClassTmp = styles.ommited;
        sectionCounter = 3;
      }
      setCastSectionCount(sectionCounter);
      setCastListClass(listClassTmp);

      // update h1's font size on desktop when number of lines >= 3
      if (!PlatformHelper.isMobile) {
        const style = window.getComputedStyle(elH1.current);
        const [lineHeight, height] = ['lineHeight', 'height'].map((prop) => parseInt(style[prop], 10));
        const numberOfLines = height / lineHeight;
        if (numberOfLines >= 3) {
          setSmallerHeadingCssClass(true);
        }
      }

      if (play.watchlist_added) {
        setOnWatchlist(true);
      }

      if (process.browser && window.navigator.userAgent.indexOf('15_0') === -1 && play?.preview) {
        setPreviewVisible(true);
      }
    }
  }, [play, setPreviewVisible]);

  const showMoreCast = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (isMobileView()) {
      setCastListClass('');
      setCastSectionCount(1000);
    } else {
      setCastDialogShowed(true);
    }
  };

  const roles = useMemo(() => sortByKeys<Role>(play?.roles, 'slug', FIRST_ROLES), [play]);
  const isLowQuality = useMemo(() => play?.video?.flags.indexOf('LOWER_QUALITY') > -1, [play?.video]);
  const structuredSEOData = useMemo(() => {
    const directors = roles.find((r) => r.name === 'Director')?.celebrities;
    const actors = roles.find((r) => r.name === 'Actor')?.celebrities;

    return {
      '@context': 'https://schema.org/',
      '@type': 'Movie',
      duration: `PT${Math.floor((play?.video?.runtime || 0) / 60)}M`,
      name: play.title,
      dateCreated: play.date_aired,
      productionCompany: {
        '@type': 'EntertainmentBusiness',
        name: play.theatre?.name,
        image: play.theatre?.poster_url,
        url: `${baseUrl(locale)}${Permalink.theatre(play.theatre, locale)}`,
      },
      director: directors?.map((p) => ({
        '@type': 'Person',
        name: p.name,
        image: p.poster_url,
        url: `${baseUrl(locale)}${Permalink.celebrity(p, locale)}`,
      })),
      actor: actors?.map((p) => ({
        '@type': 'Person',
        name: p.name,
        image: p.poster_url,
        url: `${baseUrl(locale)}${Permalink.celebrity(p, locale)}`,
      })),
      image: play.poster_url,
    };
  }, [roles, play]);

  const getDonatePart = useCallback(
    (caller: 'mobile' | 'desktop') => {
      if (!play.theatre.donations_allowed) {
        return null;
      }
      if (caller === 'mobile') {
        return (
          <div className={styles.supportMobile}>
            <div className={styles.theaterLogo} style={{ backgroundImage: `url(${play?.theatre?.poster_url})` }} />
            <h2>{translations?.supportTheater}</h2>
            <AppLink href={Permalink.donateTheatre(play?.theatre, locale)}>
              <a>
                <span>{translations?.support}</span>
              </a>
            </AppLink>
          </div>
        );
      } else if (caller === 'desktop') {
        return (
          <AppLink href={Permalink.donateTheatre(play?.theatre, locale)}>
            <a className={styles.supportDesktop}>
              <div className={styles.theaterLogo} style={{ backgroundImage: `url(${play?.theatre?.poster_url})` }} />
              <p>{translations?.support}</p>
            </a>
          </AppLink>
        );
      } else {
        return null;
      }
    },
    [translations],
  );

  const watchListText = useCallback(() => {
    return onWatchlist ? translations?.onWatchList : translations?.watchList;
  }, [onWatchlist]);

  const onWatchListBtn = useCallback(async () => {
    if (onWatchlist) {
      await getSharedApiClient().removeFromWatchlist(play.id);
      createNotification({ title: translations?.removedFromWatchList });
      setOnWatchlist(false);
      Analytics.v2_watchlistRemove(play.title, play.theatre.name);
    } else {
      await getSharedApiClient().addToWatchlist(play.id);
      createNotification({ title: translations?.addedToWatchList });
      Analytics.v2_watchlistAdd(play.title, play.theatre.name);
      setOnWatchlist(true);
    }
  }, [onWatchlist, createNotification, setOnWatchlist, getSharedApiClient]);

  const lowQuality = useCallback(() => {
    if (!isLowQuality) {
      return null;
    }
    return (
      <a
        onClick={() => {
          setPopupConfig({ popupType: PopupType.LowQuality, onClose: () => setPopupConfig(null) });
        }}
        href="#"
        id="low-quality-btn"
        className={styles.lowQuality}
      >
        <img src="/svg/ic-info.svg" alt="i" />
        <p>{t('play-detail:low_quality')}</p>
      </a>
    );
  }, [isLowQuality, setPopupConfig]);

  return (
    <Layout countryCode={countryCode}>
      <Head>
        {basicSEO(
          t('seo:performance_title', { title: play.title, theatre: play.theatre?.name }),
          play.about,
          play.poster_url,
          undefined,
          true,
          canonical,
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredSEOData),
          }}
        ></script>

        {/* <meta property="og:type" content="video.movie" /> */}

        {locales.map((langLocale) => (
          <link
            key={langLocale}
            rel="alternate"
            href={`${baseUrl(langLocale)}${Permalink.play(play, langLocale)}`}
            hrefLang={langLocale}
          />
        ))}
        <link
          key="lang-default"
          rel="alternate"
          href={`${baseUrl('en')}${Permalink.play(play, 'en')}`}
          hrefLang="x-default"
        />
      </Head>
      <main
        className={classNames(styles.playDetail, smallerHeadingCssClass ? styles.smallerHeading : null)}
        key={play.id}
      >
        <div className={styles.desktopWrapper}>
          <div className={styles.infoWrapper}>
            <div
              className={styles.image}
              style={{ backgroundImage: `url(${imageUrl(play?.poster_url, 720, undefined, 70)})` }}
            ></div>
            <ul className={styles.playMeta}>
              {play.is_original && (
                <li>
                  <a href={Permalink.dramoxOriginals(locale)} className={styles.original} title="Dramox Original">
                    Dramox Original
                  </a>
                </li>
              )}
              {play?.video?.runtime && <li className={styles.time}>{sec2time(play?.video?.runtime)}</li>}
              {play?.date_aired && (
                <li className={styles.premiere}>
                  {translations?.premiere} <strong>{formatYear(play?.date_aired, locale)}</strong>
                </li>
              )}
            </ul>
            <h1 ref={elH1}>{play?.title}</h1>
            <p>
              <AppLink href={Permalink.theatre(play?.theatre)}>
                <a>{play?.theatre.name}</a>
              </AppLink>
            </p>
            <div className={styles.buttons}>
              <PlayButton play={videoDetails ?? play} longTitle />
              <div>
                {previewVisible && (
                  <AppLink href={Permalink.preview(play, locale)}>
                    <a className={['btn', 'btn-outline-info', styles.btnWithIcon, styles.trailer].join(' ')}>
                      <span></span>
                      <span>{translations?.trailer}</span>
                    </a>
                  </AppLink>
                )}
                {user ? (
                  <a
                    onClick={onWatchListBtn}
                    className={[
                      'btn',
                      'btn-outline-info',
                      styles.btnWithIcon,
                      styles.watchList,
                      onWatchlist ? styles.checked : '',
                    ].join(' ')}
                  >
                    <span></span>
                    <span>{watchListText()}</span>
                  </a>
                ) : null}
              </div>

              {getDonatePart('desktop')}
            </div>
            <div className={styles.lowQualityMobile}>{lowQuality()}</div>
            <p className={styles.about}>{play?.about}</p>
            <div className={styles.lowQualityDesktop}>{lowQuality()}</div>

            {getDonatePart('mobile')}
            <div className={styles.genres}>
              <h2>{translations?.genres}:</h2>
              <p className={styles.tags}>
                {play?.genres.map((gnr) => (
                  <AppLink
                    key={gnr.id}
                    href={{ pathname: Permalink.genre(gnr, locale), query: { type: PlaylistType.Genres } }}
                  >
                    <a>{gnr.name}</a>
                  </AppLink>
                ))}
              </p>
            </div>

            {play?.awards.length && play?.awards[0] ? (
              <div className={styles.masonRow}>
                {play?.awards
                  ?.filter((p) => !!p)
                  .map((p, i) => {
                    const odd: React.CSSProperties = { float: 'right', clear: 'right' };

                    return (
                      <div className={styles.masonCol} style={i % 2 === 0 ? null : odd} key={i}>
                        <div className={styles.prizes}>
                          <h2>{translations?.awards}</h2>
                          <p>{p}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : null}
          </div>
          <div className={styles.castWrapper}>
            {roles?.map((role, i) => (
              <Fragment key={i}>
                <h2 className={castSectionCount < i ? styles.notShowed : ''}>{role.name}</h2>
                <ul
                  className={[styles.imageList, castListClass, castSectionCount < i ? styles.notShowed : ''].join(' ')}
                >
                  {role?.celebrities.map((celeb) => (
                    <li key={celeb.id}>
                      <AppLink href={Permalink.celebrity(celeb)}>
                        <a>
                          {getCastImg(celeb)}
                          {celeb.name}
                        </a>
                      </AppLink>
                    </li>
                  ))}
                </ul>
              </Fragment>
            ))}
            <a
              href="#"
              onClick={showMoreCast}
              className={[styles.more, castListClass === '' ? styles.notShowed : ''].join(' ')}
            >
              {translations?.showAll}
            </a>
            {castDialogShowed && (
              <CastPopup
                headline={translations.cast}
                onClose={() => {
                  setCastDialogShowed(false);
                }}
                roles={roles}
              />
            )}
          </div>
        </div>
        <div className={styles.categoriesWrapper}>
          {moreFromTheatre?.length > 0 && (
            <CategorySection
              playlistType={PlaylistType.Theater}
              title={translations?.theatreMore}
              plays={moreFromTheatre}
              moreHref={Permalink.theatre(play?.theatre, locale)}
              theater={play?.theatre.name}
            />
          )}
          {similarCastPlays?.length > 0 && (
            <CategorySection
              playlistType={PlaylistType.SameCast}
              title={translations?.sameCast}
              plays={similarCastPlays}
              moreHref={Permalink.sameCast(play, locale)}
              id={play?.id}
            />
          )}
          {similarGenresPlays?.length > 0 && (
            <CategorySection
              playlistType={PlaylistType.SameGenre}
              title={translations?.similarGenre}
              plays={similarGenresPlays}
              moreHref={Permalink.sameGenre(play, locale)}
              id={play?.id}
            />
          )}
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  let {
    params: { playSlug, slug: theatreSlug },
  } = ctx;

  const { locale, apiClient, countryCode, host, hostLocale } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  try {
    playSlug = playSlug.replace('NaN-', '');

    // ===== PLAY check =====
    const [id, ...slug] = playSlug.split('-');
    let res: PlayApi;

    // old urls without ids
    if (isNaN(+id)) {
      res = await apiClient.getPlayDetail(playSlug);
    } else {
      res = await apiClient.getPlayDetail(+id);
    }

    const play = Play.fromJSON(res);
    const localisedSlug = play.localisedSlug(locale);

    if (slug.join('-') !== localisedSlug) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.play({ ...play, id: res.id, slug: localisedSlug }, locale),
        },
      };
    }

    // ===== THEATRE check =====
    theatreSlug = theatreSlug.replace('NaN-', '');
    const [tId, ...tSlug] = theatreSlug.split('-');
    let tRes: TheatreApi;

    // old urls without ids
    if (isNaN(+tId)) {
      tRes = await apiClient.getTheatreDetail(theatreSlug);
    } else {
      tRes = await apiClient.getTheatreDetail(+tId);
    }
    const theatre = Theatre.fromJSON(tRes);

    if (tSlug.join('-') !== theatre.localisedSlug(locale)) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.play(play, locale),
        },
      };
    }

    let similarGenresPlays: PlayBase[] = [];
    let similarCastPlays: PlayBase[] = [];
    let moreFromTheatre: PlayBase[] = [];
    if (play) {
      similarGenresPlays = (await apiClient.getSimilarPlays(play?.id, { type: SimilarPlayType.Genre }))
        ?.splice(0, 10)
        ?.filter((item) => play?.id !== item.id);

      similarCastPlays = (await apiClient.getSimilarPlays(play?.id, { type: SimilarPlayType.Cast }))
        ?.splice(0, 10)
        ?.filter((item) => play?.id !== item.id);

      moreFromTheatre = ((await apiClient.getTheatreDetail(play?.theatre.id)).plays as PlayBase[])
        ?.splice(0, 10)
        ?.filter((item) => play?.id !== item.id)
        ?.map((p) => Object.assign({}, p, { theatre: play.theatre }));
    }

    const t = await getT(locale, Namespace.PlayDetail);
    const translations: T = {
      premiere: t('premiere'),
      trailer: t('trailer'),
      support: t('support'),
      genres: t('genres'),
      awards: t('awards'),
      showAll: t('show_all'),
      cast: t('cast'),
      theatreMore: t('theatre_more'),
      sameCast: t('same_cast'),
      similarGenre: t('similar_genre'),
      supportTheater: t('support_theater'),
      watchList: t('watch_list'),
      onWatchList: t('on_watch_list'),
      addedToWatchList: t('added_to_watch_list'),
      removedFromWatchList: t('removed_from_watch_list'),
    };

    const tempPlay = { ...play, theatre: tRes };

    const canonical = `https://${host}${Permalink.play(tempPlay, hostLocale)}`;

    return {
      props: { play: res, similarCastPlays, similarGenresPlays, moreFromTheatre, translations, countryCode, canonical },
    };
  } catch (e) {
    console.log(e);
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(PerformanceDetail);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/theatres/[slug]/[playSlug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  